import React, { useState } from "react";
import { updateProjet } from "../services/api";
import { dateCourte, formatDate } from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet : description et fiche
//
// Nom, dates, description, contexte, perimetre, livrable, risque, notes.
// Lecture pour tous ; modification par le chef et les co-chefs
// (droits.peut_gerer). L'import .docx reste a la creation du projet.
//
// Props : projet, collaborateurId, droits, onModifie
// ===================================================================

const SECTIONS = [
  { champ: "description", libelle: "Description", lignes: 3 },
  { champ: "contexte", libelle: "Contexte", lignes: 4 },
  { champ: "perimetre", libelle: "Périmètre", lignes: 4 },
  { champ: "livrable", libelle: "Livrable", lignes: 3 },
  { champ: "risque", libelle: "Risques", lignes: 3 },
  { champ: "notes", libelle: "Notes", lignes: 3 },
];

function valeursInitiales(projet) {
  const v = {
    nom: projet.nom || "",
    date_debut: dateCourte(projet.date_debut),
    date_fin_prevue: dateCourte(projet.date_fin_prevue),
  };
  SECTIONS.forEach((s) => { v[s.champ] = projet[s.champ] || ""; });
  return v;
}

function ProjetFicheV2({ projet, collaborateurId, droits, onModifie }) {
  const [edition, setEdition] = useState(false);
  const [valeurs, setValeurs] = useState(() => valeursInitiales(projet));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const ouvrir = () => {
    setValeurs(valeursInitiales(projet));
    setErreur(null);
    setEdition(true);
  };

  const changer = (champ) => (e) => setValeurs((v) => ({ ...v, [champ]: e.target.value }));

  const enregistrer = async () => {
    if (valeurs.date_debut && valeurs.date_fin_prevue && valeurs.date_debut > valeurs.date_fin_prevue) {
      setErreur("La date de fin prévue précède la date de début.");
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      // Seuls les champs modifies partent : le backend ne touche pas aux champs
      // absents, et efface ceux envoyes a null (une date videe, par exemple).
      const initial = valeursInitiales(projet);
      const modifies = {};
      Object.keys(valeurs).forEach((k) => {
        if (valeurs[k] === initial[k]) return;
        if (k === "nom") modifies.nom = valeurs.nom.trim();
        else modifies[k] = valeurs[k] === "" ? null : valeurs[k];
      });
      if (Object.keys(modifies).length > 0) {
        await updateProjet(projet.id, modifies, collaborateurId);
        if (onModifie) await onModifie();
      }
      setEdition(false);
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnCours(false);
    }
  };

  if (!edition) {
    return (
      <section className="v2-panneau v2-projet-bloc">
        <div className="v2-bloc-entete">
          <h2 className="v2-bloc-titre">Fiche projet</h2>
          {droits?.peut_gerer && (
            <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={ouvrir}>Modifier</button>
          )}
        </div>
        <dl className="v2-projet-fiche">
          <div className="v2-projet-fiche-ligne">
            <dt className="v2-label">Période</dt>
            <dd>{formatDate(projet.date_debut)} → {formatDate(projet.date_fin_prevue)}</dd>
          </div>
          {SECTIONS.map((s) => (
            <div key={s.champ} className="v2-projet-fiche-ligne">
              <dt className="v2-label">{s.libelle}</dt>
              <dd className={projet[s.champ] ? "v2-projet-texte" : "v2-vide"}>{projet[s.champ] || "—"}</dd>
            </div>
          ))}
        </dl>
      </section>
    );
  }

  return (
    <section className="v2-panneau v2-projet-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">Fiche projet</h2>
      </div>
      <div className="v2-formulaire">
        {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
        <div className="v2-champ">
          <label className="v2-label" htmlFor="fiche-nom">Nom<span className="v2-requis">*</span></label>
          <input id="fiche-nom" value={valeurs.nom} onChange={changer("nom")} />
        </div>
        <div className="v2-champs-ligne">
          <div className="v2-champ">
            <label className="v2-label" htmlFor="fiche-debut">Début</label>
            <input id="fiche-debut" type="date" value={valeurs.date_debut} onChange={changer("date_debut")} />
          </div>
          <div className="v2-champ">
            <label className="v2-label" htmlFor="fiche-fin">Fin prévue</label>
            <input id="fiche-fin" type="date" value={valeurs.date_fin_prevue} onChange={changer("date_fin_prevue")} />
          </div>
        </div>
        {SECTIONS.map((s) => (
          <div key={s.champ} className="v2-champ">
            <label className="v2-label" htmlFor={`fiche-${s.champ}`}>{s.libelle}</label>
            <textarea id={`fiche-${s.champ}`} rows={s.lignes} value={valeurs[s.champ]} onChange={changer(s.champ)} />
          </div>
        ))}
        <div className="v2-actions">
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setEdition(false)} disabled={enCours}>
            Annuler
          </button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={enregistrer} disabled={enCours || !valeurs.nom.trim()}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default ProjetFicheV2;
